/**
 * Live move preview — the "how many points is this worth" promise.
 *
 * 1. On every draft change the engine's `evaluateMove` runs locally WITHOUT a
 *    dictionary: an instant geometry verdict, the formed words and the score.
 * 2. The formed words are then checked against the server dictionary via
 *    `dict:check`, debounced (~150 ms) and cached per word, so each word is
 *    only ever asked about once per session.
 */
import { DICT_CHECK_MAX_WORDS, evaluateMove, messages } from '@wordloom/engine';
import type { Board, Coord, FormedWord, MoveEvaluation, Placement, Tile } from '@wordloom/engine';
import type { DraftPlacement } from './draft';

// ---------------------------------------------------------------------------
// Dictionary cache
// ---------------------------------------------------------------------------

/** Performs the lookups; resolves null on failure (the words stay unknown). */
export type DictChecker = (words: string[]) => Promise<Record<string, boolean> | null>;

export type WordStatus = 'valid' | 'invalid' | 'checking';

export class DictCache {
  private results = new Map<string, boolean>();
  private inFlight = new Set<string>();
  private queued = new Set<string>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private listeners = new Set<() => void>();
  /** Increments on every change (useSyncExternalStore snapshot). */
  version = 0;

  constructor(
    private readonly check: DictChecker,
    private readonly debounceMs = 150,
  ) {}

  /** true / false once known, undefined while unknown. */
  get(word: string): boolean | undefined {
    return this.results.get(word.toUpperCase());
  }

  status(word: string): WordStatus {
    const known = this.get(word);
    if (known === true) return 'valid';
    if (known === false) return 'invalid';
    return 'checking';
  }

  /** Record a verdict we already know (e.g. words the server accepted in play). */
  seed(word: string, valid: boolean): void {
    const upper = word.toUpperCase();
    if (this.results.get(upper) === valid) return;
    this.results.set(upper, valid);
    this.changed();
  }

  /** Queue lookups for unknown words; fires one batched request after the debounce. */
  request(words: readonly string[]): void {
    let added = false;
    for (const raw of words) {
      const w = raw.toUpperCase();
      if (w.length < 2 || !/^[A-Z]+$/.test(w)) continue;
      if (this.results.has(w) || this.inFlight.has(w) || this.queued.has(w)) continue;
      this.queued.add(w);
      added = true;
    }
    if (!added) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.flush();
    }, this.debounceMs);
  }

  /** Send everything queued now (chunked to the protocol's per-request cap). */
  async flush(): Promise<void> {
    const words = [...this.queued];
    this.queued.clear();
    if (words.length === 0) return;
    for (const w of words) this.inFlight.add(w);
    const chunks: string[][] = [];
    for (let i = 0; i < words.length; i += DICT_CHECK_MAX_WORDS) chunks.push(words.slice(i, i + DICT_CHECK_MAX_WORDS));
    await Promise.all(
      chunks.map(async (chunk) => {
        let result: Record<string, boolean> | null = null;
        try {
          result = await this.check(chunk);
        } catch {
          result = null;
        }
        for (const w of chunk) {
          this.inFlight.delete(w);
          const verdict = result?.[w] ?? result?.[w.toLowerCase()];
          // Unknown on failure: not cached, so a later request asks again.
          if (typeof verdict === 'boolean') this.results.set(w, verdict);
        }
      }),
    );
    this.changed();
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private changed(): void {
    this.version++;
    for (const l of this.listeners) l();
  }
}

// ---------------------------------------------------------------------------
// Evaluation + summary
// ---------------------------------------------------------------------------

/** evaluateMove for the draft (null when nothing is placed or the engine fails). */
export function evaluateDraft(
  board: Board,
  placements: readonly DraftPlacement[],
  rack: readonly Tile[],
): MoveEvaluation | null {
  if (placements.length === 0) return null;
  try {
    return evaluateMove(board, placements as readonly Placement[], rack);
  } catch {
    return null;
  }
}

export interface PreviewWord extends FormedWord {
  status: WordStatus;
}

export interface PreviewModel {
  /** empty: nothing placed; invalid: rule or dictionary failure; checking: awaiting dict:check. */
  state: 'empty' | 'invalid' | 'checking' | 'valid';
  words: PreviewWord[];
  /** Points if played (null when the geometry is invalid). */
  total: number | null;
  bingo: boolean;
  bingoBonus: number;
  /** Plain-language reason when invalid. */
  reason: string | null;
  /**
   * 'info' when the reason is a next step rather than a mistake (a blank still
   * waiting for its letter): styled neutrally, nothing ringed red.
   */
  tone?: 'info';
  /** Squares to ring red. */
  badCells: Coord[];
}

export function summarizePreview(evaluation: MoveEvaluation | null, lookup: (word: string) => WordStatus): PreviewModel {
  if (!evaluation) {
    return { state: 'empty', words: [], total: null, bingo: false, bingoBonus: 0, reason: null, badCells: [] };
  }
  if (!evaluation.ok) {
    const words = (evaluation.words ?? []).map((w) => ({ ...w, status: lookup(w.word) }));
    const nextStep = evaluation.error.code === 'BLANK_NEEDS_LETTER';
    return {
      state: 'invalid',
      words,
      total: evaluation.total ?? null,
      bingo: false,
      bingoBonus: 0,
      reason: evaluation.error.message,
      ...(nextStep ? { tone: 'info' as const } : {}),
      badCells: nextStep ? [] : (evaluation.error.cells ?? []),
    };
  }
  const words: PreviewWord[] = evaluation.words.map((w) => ({ ...w, status: lookup(w.word) }));
  const invalid = words.filter((w) => w.status === 'invalid');
  const base = { words, total: evaluation.total, bingo: evaluation.bingo, bingoBonus: evaluation.bingoBonus };
  if (invalid.length > 0) {
    const badCells = invalid.flatMap((w) => w.cells.map((c) => ({ row: c.row, col: c.col })));
    return { ...base, state: 'invalid', reason: messages.invalidWords(invalid.map((w) => w.word)), badCells };
  }
  if (words.some((w) => w.status === 'checking')) return { ...base, state: 'checking', reason: null, badCells: [] };
  return { ...base, state: 'valid', reason: null, badCells: [] };
}

/**
 * The one-line summary in two parts: `head` (the words, or the whole line)
 * and `tail` (" · 24 pts"), so the phone strip can ellipsize a long word list
 * and still show the total. With several words each carries its points:
 * "NO 3, NA 3, OM 4 · 10 pts". An invalid move keeps the words short
 * ("SLOPE + 2 more · …") so its reason fits the strip's two lines.
 */
export function previewParts(model: PreviewModel): { head: string; tail: string } {
  if (model.state === 'empty') return { head: 'Place tiles to see your score.', tail: '' };
  if (model.state === 'invalid' && model.total == null) {
    return { head: model.reason ?? 'That placement isn’t valid.', tail: '' };
  }
  if (model.state === 'invalid') {
    const main = model.words[0]?.word ?? '';
    const more = model.words.length - 1;
    return { head: `${more > 0 ? `${main} + ${more} more` : main} · ${model.reason ?? 'not valid'}`, tail: '' };
  }
  const head =
    model.words.length > 1 ? model.words.map((w) => `${w.word} ${w.score}`).join(', ') : (model.words[0]?.word ?? '');
  const pts = model.total != null ? `${model.total} pts` : '';
  return { head, tail: ` · ${pts}${model.bingo ? ' · bingo!' : ''}` };
}

/** One-line summary for the mobile strip and the aria-live region. */
export function previewLine(model: PreviewModel): string {
  const { head, tail } = previewParts(model);
  return head + tail;
}

/**
 * Breakdown for a word chip's tooltip: "SLOPE: 9 × 2W = 18", or just
 * "UP: 5" when no word multiplier applies (the total would only repeat).
 * A blank on a letter premium is not listed: it scores 0 however it is multiplied.
 */
export function wordBreakdown(word: FormedWord): string {
  const letterPremiums = word.cells.filter(
    (c) => c.isNew && !c.blank && (c.premium === 'DL' || c.premium === 'TL'),
  );
  const wordPremiums = word.cells.filter((c) => c.isNew && (c.premium === 'DW' || c.premium === 'TW'));
  let text = `${word.word}: ${word.letterSum}`;
  if (letterPremiums.length) {
    text += ` (incl. ${letterPremiums.map((c) => `${c.letter}×${c.premium === 'TL' ? 3 : 2}`).join(', ')})`;
  }
  if (word.wordMultiplier <= 1) return text;
  const labels = wordPremiums.map((c) => (c.premium === 'TW' ? '3W' : '2W')).join(' × ');
  return `${text} × ${labels} = ${word.score}`;
}
